import { LocationChangeTimeZone } from '../api/models'
import { IssueMessageForField } from '../pages/address-scheduler/AddressSchedulerFormPage/addressScheduleFormUtils'

export * from './AgencyPermitRequirementDisplayNames'
export * from './PermitSource'
export * from './PropertyTypeDisplayNames'
export * from './SimplisafeTimeZoneName'
export * from './scopes'

export const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000

/**
 * A constant representing the absence of data, displayed as an em dash (—) character.
 * `\u2014` is the unicode representation for an em dash. Normally we'd want to use
 * the '&mdash;' html entity code, but React escapes it and it ends up rendering as
 * the literal string '&mdash;' instead of '—'
 */
export const MISSING_VALUE = '\u2014'

/**
 * The default GC Time for the query client, expressed in milliseconds.
 */
export const DEFAULT_GC_TIME = 30 * ONE_MINUTE_IN_MILLISECONDS

/**
 * The default stale time for the query client, expressed in milliseconds.
 */
export const DEFAULT_STALE_TIME = 15 * ONE_MINUTE_IN_MILLISECONDS

/**
 * The links that can be accessed through the location info box.
 */
export const LOCATION_INFO_LINKS = {
  CSR_PAGE: (uid: string, env: string) => {
    return `https://${env}.simplisafe.com/admin/customer-service/csr/${uid}`
  },
  SERVICE_PAGE: (sid: string, env: string) => {
    return `https://${env}.simplisafe.com/admin/store/service/subscription/edit/${sid}`
  }
} as const

/**
 * The links that can be accessed through the permit location component.
 */
export const PERMIT_LOCATION_INFO_LINKS = {
  SUPPORT_HUB_PAGE: (uid: string, sid: string) => {
    const envName = dex.config.name
    return `https://support.${envName}.ops.simplisafe.com/account-overview?uid=${uid}&sid=${sid}`
  }
} as const

export const ALL_APPLET_NAMES = {
  PERMIT_MANAGEMENT: 'Permit Management',
  ALARM_SMS_REVIEW: 'Alarm SMS Review',
  ENGAGEMENT_REVIEW_TOOL: 'Engagement Review Tool',
  ADDRESS_SCHEDULER: 'Address Scheduler',
  MONITORING_MODERATION: 'Monitoring Moderation',
  DEVTOOLS: 'Dev Tools'
}

/**
 * The properties needed to authenticate in the Auth0 service.
 */
export const AUTH_PROPERTIES = {
  /**
   * The audience needed for authentication.
   */
  MUSEUM_AUDIENCE: 'https://api.monitoring.simplisafe.com/'
} as const

/**
 * The sidebar name for the PermitsTab page.
 */
export const SIDEBAR_PERMITS = 'PermitsTab'

/**
 * The sidebar name for the Jurisdiction Details page.
 */
export const SIDEBAR_JURISDICTION_DETAILS = 'Jurisdiction Details'

/**
 * The names of the pages for the sidebar component.
 */
export const SIDEBAR_PAGE_NAMES = [SIDEBAR_PERMITS, SIDEBAR_JURISDICTION_DETAILS]

/**
 * The mappings between smarty error types and the messages with them.
 */
export const ADDRESS_ISSUE_MAPPINGS: Record<string, IssueMessageForField> = {
  UniqueZip: {
    helperText: '',
    fieldName: 'postalCode'
  },
  UnrecognizedSecondaryAddress: {
    helperText: 'invalid apartment, suite, etc',
    fieldName: 'street2'
  },
  MagnetStreetSyndrome: {
    helperText: 'Missing directional prefix (N, S, E, W)',
    fieldName: 'street1'
  }
}

export const RESIDENCE_TYPE_ARRAY = ['House', 'Apartment', 'Commercial']

export const VALID_TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Puerto_Rico',
  'America/Phoenix',
  'Pacific/Honolulu',
  'America/Anchorage',
  'Europe/London',
  'Pacific/Guam'
] as LocationChangeTimeZone[]

export const PRACTICE_MODE_DURATIONS = [
  { label: '15 minutes', millis: 15 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '30 minutes', millis: 30 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '45 minutes', millis: 45 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '1 hour', millis: 1 * 60 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '2 hours', millis: 2 * 60 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '3 hours', millis: 3 * 60 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '4 hours', millis: 4 * 60 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '8 hours', millis: 8 * 60 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '1 day', millis: 24 * 60 * ONE_MINUTE_IN_MILLISECONDS },
  { label: '3 days', millis: 3 * 24 * 60 * ONE_MINUTE_IN_MILLISECONDS }
] as const

/**
 * Represents a default value indicating an unset or invalid address validation result.
 * @constant
 * @type {number}
 */
export const SELECTED_ADDRESS_UNVERIFIED = 'UNVERIFIED'

export const STATE_ABBREVIATION_MAP: { [key: string]: string } = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  PR: 'Puerto Rico',
  GU: 'Guam',
  VI: 'U.S. Virgin Islands'
}

export const STREET_SUFFIX_MAP: { [key: string]: string } = {
  ALLEY: 'Aly',
  AVENUE: 'Ave',
  BOULEVARD: 'Blvd',
  CIRCLE: 'Cir',
  COURT: 'Ct',
  DRIVE: 'Dr',
  EXPRESSWAY: 'Expy',
  GARDENS: 'Gdns',
  HIGHWAY: 'Hwy',
  LANE: 'Ln',
  PARKWAY: 'Pkwy',
  PLACE: 'Pl',
  PLAZA: 'Plz',
  ROAD: 'Rd',
  SQUARE: 'Sq',
  STREET: 'St',
  TERRACE: 'Ter',
  WAY: 'Way'
}

export const MODERATION_EVENT_SOURCE = 'Moderation Tool'
